import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Send, CheckCircle, AlertCircle, ArrowRight, ChevronLeft } from 'lucide-react';
import { Input, Textarea, Select, Checkbox } from './Input';
import Button from './Button';
import { motion, AnimatePresence } from 'framer-motion';

// Stage 1 Validation Schema (Quick Inquiry - Low Barrier)
const stage1Schema = z.object({
  company: z.string().min(2, 'Firmenname ist erforderlich'),
  email: z.string().email('Ungültige E-Mail-Adresse'),
  serviceType: z.string().min(1, 'Bitte wählen Sie eine Anfrageart'),
  message: z.string().min(10, 'Bitte beschreiben Sie Ihre Anforderungen (min. 10 Zeichen)')
});

// Stage 2 Validation Schema (Complete Details)
const stage2Schema = z.object({
  firstName: z.string().min(2, 'Vorname ist erforderlich'),
  lastName: z.string().min(2, 'Nachname ist erforderlich'),
  phone: z.string().min(7, 'Telefonnummer ist erforderlich'),
  postalCode: z.string().min(5, 'PLZ ist erforderlich'),
  city: z.string().min(2, 'Ort ist erforderlich'),
  startDate: z.string().optional(),
  privacyConsent: z.boolean().refine(val => val === true, {
    message: 'Sie müssen der Datenschutzerklärung zustimmen'
  }),
  phoneConsent: z.boolean().optional()
});

// Combined Schema for final submission
const completeSchema = stage1Schema.merge(stage2Schema);

const ContactForm = ({ compact = false }) => {
  const [currentStage, setCurrentStage] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [stage1Data, setStage1Data] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    trigger,
    getValues
  } = useForm({
    resolver: zodResolver(currentStage === 1 ? stage1Schema : completeSchema),
    mode: 'onBlur'
  });

  const serviceTypeOptions = [
    { value: '', label: 'Bitte wählen...' },
    { value: 'personal', label: 'Personalüberlassung' },
    { value: 'event', label: 'Event-Aufbau / Messe' },
    { value: 'facility', label: 'Gebäudemanagement' },
    { value: 'cleaning', label: 'Reinigung / Bauendreinigung' },
    { value: 'demolition', label: 'Abbrucharbeiten' },
    { value: 'public', label: 'Öffentliche Ausschreibung' },
    { value: 'hospitality', label: 'Hostessen / Servicepersonal' },
    { value: 'other', label: 'Sonstiges' }
  ];

  // Handle Stage 1 completion
  const handleStage1Submit = async (e) => {
    e.preventDefault();

    // Validate Stage 1 fields
    const isValid = await trigger(['company', 'email', 'serviceType', 'message']);

    if (isValid) {
      // Save Stage 1 data
      const data = getValues();
      setStage1Data({
        company: data.company,
        email: data.email,
        serviceType: data.serviceType,
        message: data.message
      });

      // Move to Stage 2
      setCurrentStage(2);

      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle going back to Stage 1
  const handleBackToStage1 = () => {
    setCurrentStage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle final submission
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Combine Stage 1 and Stage 2 data
      const completeData = {
        ...stage1Data,
        ...data
      };

      // Submit to Azure Function API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeData),
      });

      const responseData = await response.json();

      if (response.ok && responseData.success) {
        setSubmitStatus('success');
        reset();
        setCurrentStage(1);
        setStage1Data(null);

        // Scroll to success message
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);

        // Hide success message after 5 seconds
        setTimeout(() => setSubmitStatus(null), 5000);
      } else {
        throw new Error(responseData.error || 'Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${compact ? 'max-w-2xl mx-auto' : 'max-w-4xl mx-auto'}`}>
      {/* Success/Error Messages - Always visible at top */}
      {submitStatus === 'success' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border-2 border-green-500 rounded-xl p-6 mb-8 flex items-start space-x-4 shadow-lg"
        >
          <CheckCircle className="w-8 h-8 text-green-600 flex-shrink-0" />
          <div>
            <p className="text-lg font-bold text-green-900 mb-2">
              ✅ Vielen Dank für Ihre Anfrage!
            </p>
            <p className="text-base text-green-800">
              Wir haben Ihre Nachricht erhalten und werden uns schnellstmöglich bei Ihnen melden.
              In der Regel erfolgt eine Rückmeldung innerhalb von 24 Stunden.
            </p>
          </div>
        </motion.div>
      )}

      {submitStatus === 'error' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-2 border-red-500 rounded-xl p-6 mb-8 flex items-start space-x-4 shadow-lg"
        >
          <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
          <div>
            <p className="text-lg font-bold text-red-900 mb-2">
              ❌ Ein Fehler ist aufgetreten
            </p>
            <p className="text-base text-red-800">
              Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt per E-Mail oder Telefon.
            </p>
          </div>
        </motion.div>
      )}

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-4 mb-4">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              currentStage === 1
                ? 'bg-primary-blue-mid text-white'
                : 'bg-green-600 text-white'
            }`}>
              {currentStage === 1 ? '1' : '✓'}
            </div>
            <span className={`ml-3 font-medium ${
              currentStage === 1 ? 'text-primary-text-dark' : 'text-primary-text-muted'
            }`}>
              Quick Inquiry
            </span>
          </div>

          <div className={`w-24 h-1 ${
            currentStage === 2 ? 'bg-primary-blue-mid' : 'bg-primary-gray-border'
          }`} />

          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
              currentStage === 2
                ? 'bg-primary-blue-mid text-white'
                : 'bg-primary-gray-border text-primary-text-muted'
            }`}>
              2
            </div>
            <span className={`ml-3 font-medium ${
              currentStage === 2 ? 'text-primary-text-dark' : 'text-primary-text-muted'
            }`}>
              Details
            </span>
          </div>
        </div>

        <p className="text-center text-sm text-primary-text-muted">
          {currentStage === 1
            ? 'Nur 4 Felder - Schnelle Anfrage in unter 60 Sekunden'
            : 'Fast geschafft! Bitte vervollständigen Sie Ihre Kontaktdaten'}
        </p>
      </div>

      <AnimatePresence mode="wait">
        {/* STAGE 1: Quick Inquiry */}
        {currentStage === 1 && (
          <motion.div
            key="stage1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            <form onSubmit={handleStage1Submit} className="space-y-6">
              {/* Stage 1 Fields - Minimal Barrier */}
              <div className="bg-white rounded-xl p-6 md:p-8 shadow-soft border border-primary-gray-border">
                <h3 className="text-xl font-semibold text-primary-text-dark mb-6">
                  Schnellanfrage - Kostenloses Angebot erhalten
                </h3>

                <div className="space-y-5">
                  <Input
                    label="Firmenname"
                    {...register('company')}
                    error={errors.company?.message}
                    placeholder="Ihre Firma GmbH"
                    required
                  />

                  <Input
                    label="E-Mail-Adresse"
                    type="email"
                    {...register('email')}
                    error={errors.email?.message}
                    placeholder="kontakt@ihre-firma.de"
                    required
                  />

                  <Select
                    label="Welche Leistung benötigen Sie?"
                    {...register('serviceType')}
                    error={errors.serviceType?.message}
                    options={serviceTypeOptions}
                    required
                  />

                  <Textarea
                    label="Beschreiben Sie kurz Ihren Bedarf"
                    {...register('message')}
                    error={errors.message?.message}
                    rows={4}
                    placeholder="z.B. Wir suchen 5 Produktionshelfer für 3 Monate in München..."
                    required
                  />
                </div>

                <div className="mt-6 p-4 bg-primary-blue-light/30 rounded-lg">
                  <p className="text-sm text-primary-text-dark">
                    <strong>✓ Kostenlos & unverbindlich</strong> – Wir erstellen Ihnen ein maßgeschneidertes Angebot innerhalb von 24 Stunden.
                  </p>
                </div>
              </div>

              {/* Stage 1 Submit Button */}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  icon={ArrowRight}
                  iconPosition="right"
                >
                  Angebot anfordern
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {/* STAGE 2: Complete Details */}
        {currentStage === 2 && (
          <motion.div
            key="stage2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
          >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Summary of Stage 1 */}
              <div className="bg-primary-skin/40 rounded-xl p-6 border border-primary-gray-border">
                <h4 className="font-semibold text-primary-text-dark mb-3">
                  Ihre Anfrage:
                </h4>
                <div className="space-y-1 text-sm text-primary-text-muted">
                  <p><strong>Firma:</strong> {stage1Data?.company}</p>
                  <p><strong>E-Mail:</strong> {stage1Data?.email}</p>
                  <p><strong>Service:</strong> {serviceTypeOptions.find(opt => opt.value === stage1Data?.serviceType)?.label}</p>
                </div>
                <button
                  type="button"
                  onClick={handleBackToStage1}
                  className="mt-3 text-sm text-primary-blue-mid hover:underline flex items-center"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Ändern
                </button>
              </div>

              {/* Contact Person Details */}
              <div className="bg-white rounded-xl p-6 md:p-8 shadow-soft border border-primary-gray-border">
                <h3 className="text-xl font-semibold text-primary-text-dark mb-6">
                  Ihre Kontaktdaten
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input
                    label="Vorname"
                    {...register('firstName')}
                    error={errors.firstName?.message}
                    placeholder="Max"
                    required
                  />
                  <Input
                    label="Nachname"
                    {...register('lastName')}
                    error={errors.lastName?.message}
                    placeholder="Mustermann"
                    required
                  />
                  <Input
                    label="Telefon"
                    type="tel"
                    {...register('phone')}
                    error={errors.phone?.message}
                    placeholder="+49 89 12345678"
                    required
                  />
                  <Input
                    label="Gewünschtes Startdatum"
                    type="date"
                    {...register('startDate')}
                    error={errors.startDate?.message}
                  />
                  <Input
                    label="PLZ"
                    {...register('postalCode')}
                    error={errors.postalCode?.message}
                    placeholder="80331"
                    required
                  />
                  <Input
                    label="Ort"
                    {...register('city')}
                    error={errors.city?.message}
                    placeholder="München"
                    required
                  />
                </div>
              </div>

              {/* Consent */}
              <div className="bg-white rounded-xl p-6 shadow-soft border border-primary-gray-border space-y-4">
                <Checkbox
                  label={
                    <span className="text-sm">
                      Ich habe die{' '}
                      <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-primary-blue-mid hover:underline">
                        Datenschutzerklärung
                      </a>{' '}
                      gelesen und stimme der Verarbeitung meiner Daten zu. *
                    </span>
                  }
                  {...register('privacyConsent')}
                  error={errors.privacyConsent?.message}
                />
                <Checkbox
                  label="Ich möchte zusätzlich telefonisch kontaktiert werden."
                  {...register('phoneConsent')}
                />
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  icon={ChevronLeft}
                  onClick={handleBackToStage1}
                >
                  Zurück
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  icon={Send}
                  iconPosition="right"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Anfrage absenden'}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Trust Indicators */}
      {currentStage === 1 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="p-4 bg-white rounded-lg border border-primary-gray-border">
            <div className="text-2xl mb-2">⚡</div>
            <div className="text-sm font-semibold text-primary-text-dark">Schnelle Antwort</div>
            <div className="text-xs text-primary-text-muted">Innerhalb 24h</div>
          </div>
          <div className="p-4 bg-white rounded-lg border border-primary-gray-border">
            <div className="text-2xl mb-2">✓</div>
            <div className="text-sm font-semibold text-primary-text-dark">Unverbindlich</div>
            <div className="text-xs text-primary-text-muted">Kostenloses Angebot</div>
          </div>
          <div className="p-4 bg-white rounded-lg border border-primary-gray-border">
            <div className="text-2xl mb-2">🔒</div>
            <div className="text-sm font-semibold text-primary-text-dark">DSGVO-konform</div>
            <div className="text-xs text-primary-text-muted">Ihre Daten sind sicher</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactForm;
